import findObjectInArray from '@afosto/utils/array/findObjectInArray';

/**
 * Return an active sorting by id.
 * @param {string} key
 * @return {object|undefined}
 */
const getActiveSorting = key => ({ activeSort }) => findObjectInArray(activeSort, 'key', key);

/**
 * Remove an active sorting.
 * @param {string} key
 */
const removeActiveSorting = key => ({ setActiveSort, activeSort }) => {
  setActiveSort(activeSort.filter(sort => sort.key !== key));
};

/**
 * Update an active sorting.
 * @param {object} activeSorting
 */
const updateActiveSorting = activeSorting => ({ setActiveSort, activeSort }) => {
  const sortingKey = activeSorting.currentKey || activeSorting.key;
  const currentIndex = activeSort.findIndex(listItem => listItem.key === sortingKey);
  const otherValues = activeSort.filter(listItem => listItem.key !== sortingKey);
  const newValues = [...otherValues];

  newValues.splice(currentIndex, 0, activeSorting);
  setActiveSort(newValues);
};

/**
 * Add selection to active sorting.
 * @param {object} activeSorting
 */
const addActiveSorting = activeSorting => state => {
  const { setActiveSort, activeSort } = state;
  const sortingKey = activeSorting.currentKey || activeSorting.key;
  const sort = getActiveSorting(sortingKey)(state);

  if (sort) {
    updateActiveSorting(activeSorting)(state);
  } else {
    setActiveSort([...activeSort, activeSorting]);
  }
};

/**
 * Clear all active sorting.
 */
const clearActiveSorting = () => ({ setActiveSort }) => {
  setActiveSort([]);
};

/**
 * Clear the sort selection.
 */
const clearSortSelection = () => ({ setSortSelection }) => {
  setSortSelection(null);
};

/**
 * Save the sort selection.
 */
const saveSortSelection = () => state => {
  const { key, currentKey, direction } = state.sortSelection;
  const { isNew, ...otherProps } = state.sortSelection;

  if (!key || !direction) {
    return;
  }

  const sortingKey = currentKey || key;
  const activeSorting = getActiveSorting(sortingKey)(state);

  if (activeSorting) {
    updateActiveSorting(otherProps)(state);
  } else {
    addActiveSorting(otherProps)(state);
  }

  state.setSortSelection(null);
};

/**
 * Return all available (not selected) sort options.
 * @returns {function({activeSort?: *, sortOptions: *}): *}
 */
const getAvailableSortOptions = () => ({ activeSort, sortOptions, sortSelection }) =>
  sortOptions.filter(
    sortOption =>
      !findObjectInArray(activeSort, 'key', sortOption.key) ||
      (sortSelection && sortSelection.currentKey === sortOption.key),
  );

/**
 * Return a sort option for the provided key.
 * @param {string} sortOptionKey
 * @returns {function({sortOptions: *}): *}
 */
const getSortOption = sortOptionKey => ({ sortOptions }) =>
  findObjectInArray(sortOptions, 'key', sortOptionKey);

/**
 * Return a sort option name for the provided key.
 * @param {string} sortOptionKey
 * @returns {Function}
 */
const getSortOptionName = sortOptionKey => ({ sortOptions }) => {
  const sortOption = findObjectInArray(sortOptions, 'key', sortOptionKey);

  if (sortOption) {
    return sortOption.name;
  }

  return 'Unknown';
};

/**
 * Returns whether there is active sorting.
 * @returns {function({activeSort: *}): boolean}
 */
const hasActiveSorting = () => ({ activeSort }) => activeSort.length > 0;

/**
 * Returs whether there are available search options.
 * @returns {function({activeSort?: *, sortOptions: *}): boolean}
 */
const hasAvailableSortOptions = () => state => {
  const availableSortOptions = getAvailableSortOptions()(state);
  return availableSortOptions.length > 0;
};

export default {
  getActiveSorting,
  addActiveSorting,
  removeActiveSorting,
  updateActiveSorting,
  clearActiveSorting,
  clearSortSelection,
  saveSortSelection,
  getAvailableSortOptions,
  hasActiveSorting,
  hasAvailableSortOptions,
  getSortOption,
  getSortOptionName,
};
